import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { MainButton } from "../../components/MainButton";
import { BookingHeader } from "../../components/BookingHeader";
import { ConfigType } from "../../contexts/AppConfigContext";
import { useAppConfig } from "../../hooks/useAppConfig";
import { usePluginspace } from "../../hooks/usePluginspace";
import "./index.css";
import Swal from "sweetalert2";
import api from "../../services/api";
import InputMask from "react-input-mask";
import { Checkbox } from "../../components/Checkbox";
import { telephoneNoMask } from "../../functions/mask";
import { getWeekDay, translateWeekday } from "../../functions/util";
import { Sparkles, MapPin, Users, Calendar, Clock, User, Phone, Mail, Accessibility, DollarSign, FileText } from 'lucide-react';
import { ModalTerms } from "../../components/ModalTerms";
import { AccessibilityComponent } from "./component/Accessibility";

// Importar fonte Poppins
if (typeof document !== 'undefined') {
  const link = document.createElement('link');
  link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap';
  link.rel = 'stylesheet';
  if (!document.head.querySelector('link[href*="Poppins"]')) {
    document.head.appendChild(link);
  }

  // Configurar viewport para mobile responsivo
  let viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) {
    viewport = document.createElement('meta');
    viewport.setAttribute('name', 'viewport');
    document.head.appendChild(viewport);
  }
  viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
}

type ImageType = {
  id: string;
  url: string;
};

type HourType = {
  id: string;
  hour: string;
};

type DayType = {
  able: boolean;
  day:
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday";
  hours: HourType[];
};

type PriceGroupRange = {
  minPeople: number;
  maxPeople: number;
  price: number;
};

type EnvironmentType = {
  id: string;
  name: string;
  seatsAvailable: number;
  tables: number;
  pricePerSeat?: number | null;
  pricePerEnvironment?: number | null;
  pricePerTable?: number | null;
  priceByGroup?: PriceGroupRange[] | null;
};

type BookingConfigType = {
  id: string;
  able: boolean;
  dayConfirm: boolean;
  expiryTime: number;
  maxPeople: number;
  paymentConfirm: boolean;
  valuePaymentConfirm: number;
  days: DayType[];
  images: ImageType[];
  environment: EnvironmentType[];
  useTerms: boolean;
};

const AccessibilityOptions: {
  description: string;
  value: 'Apoio ao Idoso' | 'Cadeirante' | 'PCD' | 'Autismo' | 'Gestantes ' | 'Obeso ' | null;
}[] = [
  { value: 'Apoio ao Idoso', description: 'Assistência especial para pessoas idosas' },
  { value: 'Cadeirante', description: 'Acesso facilitado para cadeirantes' },
  { value: 'PCD', description: 'Apoio para pessoas PCD' },
  //{ value: 'Autismo', description: 'Apoio para pessoas com autismo' },
  { value: 'Gestantes ', description: 'Apoio para gestantes' },
  { value: 'Obeso ', description: 'Apoio para pessoas obesas' },
]

export function Booking() {
  const { pluginspace_id } = useParams();
  const { registerAccess, getToken, getThisPluginspace } = usePluginspace();
  const navigate = useNavigate();

  const { getAppConfig } = useAppConfig();
  const [config, setConfig] = useState<ConfigType | void>();
  const [loading, setLoading] = useState(true);
  const [bookingConfig, setBookingConfig] = useState<BookingConfigType | null>(
    null
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dateBooking, setDateBooking] = useState("");
  const [hours, setHours] = useState([
    { value: 0, select: "Selecione um horário" },
  ]);
  const [hourId, setHourId] = useState(0);
  const [environmentId, setEnvironmentId] = useState(0);
  const [people, setPeople] = useState(0);
  const [phone, setPhone] = useState("");
  const [phoneFind, setPhoneFind] = useState("");
  const [maskTelephone, setMaskTelephone] = useState("(99) 99999-9999");
  const [maskTelephoneFind, setMaskTelephoneFind] = useState("(99) 99999-9999");
  const [acceptTerm, setAcceptTerm] = useState(false);
  const [term, setTerm] = useState("");
  const [openTerms, setOpenTerms] = useState(false);

  // Estado para necessidades de acessibilidade
  const [accessibilityNeeds, setAccessibilityNeeds] = useState<'Apoio ao Idoso' | 'Cadeirante' | 'PCD' | 'Autismo' | 'Gestantes ' | 'Obeso ' | null>(null);

  // Estados para cálculo de preço
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const [priceInReais, setPriceInReais] = useState<number>(0);
  const [priceType, setPriceType] = useState<string>("");
  const [priceBreakdown, setPriceBreakdown] = useState<any>(null);
  const [requiresPayment, setRequiresPayment] = useState<boolean>(false);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    registerAccess("booking");
    init();
  }, []);

  // 🎠 CARROSEL: Rotaciona entre as imagens a cada 5 segundos
  useEffect(() => {
    if (bookingConfig && bookingConfig.images && bookingConfig.images.length > 0) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) =>
          (prevIndex + 1) % bookingConfig.images.length
        );
      }, 5000); // Muda a imagem a cada 5 segundos
      return () => clearInterval(interval);
    }
  }, [bookingConfig]);

  async function init() {
    setLoading(true);
    localStorage.removeItem("bookingToPay");
    await getConfig();
    await getBookingConfig();
    await getTerms();
    setLoading(false);
  }

  async function getTerms() {
    await api
      .post(
        `/api/consent/filter`,
        {
          active: true,
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then(async (response) => {
        if (response.data.data && response.data.data[0]) {
          setTerm(response.data.data[0].content);
        } else {
          setTerm("");
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  async function getBookingConfig() {
    await api
      .get("/api/queueReservation/bookingConfig", {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then((response) => {
        const bookingResponse = response.data;
        if (
          bookingResponse &&
          bookingResponse.data &&
          bookingResponse.data[0]
        ) {
          const bookingConfigItem: BookingConfigType = bookingResponse.data[0];
          if (bookingConfigItem && bookingConfigItem.able) {
            setBookingConfig(bookingConfigItem);
          } else {
            Swal.fire({
              icon: "error",
              title: "Reserva não está habilitada!",
              text: "Loja não está com o esquema de reservas habilitada.",
            }).then(() => navigate(`/${pluginspace_id}`));
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Reserva não configurada!",
            text: "Loja não está com o esquema de reserva configurada.",
          }).then(() => navigate(`/${pluginspace_id}`));
        }
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          icon: "error",
          title: "Reserva não configurada!",
          text: "Loja não está com o esquema de reservas configurada.",
        }).then(() => navigate(`/${pluginspace_id}`));
      });
  }

  // Função para calcular o preço da reserva chamando o backend
  async function calculatePrice() {
    if (!environmentId || people <= 0) {
      setCalculatedPrice(null);
      setPriceInReais(0);
      setPriceType("");
      setPriceBreakdown(null);
      setRequiresPayment(false);
      return;
    }

    setCalculatingPrice(true);

    try {
      const response = await api.post(
        "/api/queueReservation/booking/calculate-price",
        {
          bookingEnvironmentId: environmentId,
          people: people,
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      );

      if (response.data) {
        setCalculatedPrice(response.data.price);
        setPriceInReais(response.data.priceInReais);
        setPriceType(response.data.priceType);
        setPriceBreakdown(response.data.breakdown);
        setRequiresPayment(response.data.requiresPayment);
      }
    } catch (err) {
      console.error("Erro ao calcular preço:", err);
      // Em caso de erro, resetar os valores
      setCalculatedPrice(null);
      setPriceInReais(0);
      setPriceType("");
      setPriceBreakdown(null);
      setRequiresPayment(false);
    } finally {
      setCalculatingPrice(false);
    }
  }

  // useEffect para recalcular preço quando ambiente ou pessoas mudarem
  useEffect(() => {
    if (environmentId && people > 0) {
      calculatePrice();
    }
  }, [environmentId, people]);

  async function goBooking() {
    if (people <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `Precisa haver uma pessoa ou mais na reserva.`,
      });
      return;
    }

    if (!name) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário adicionar um nome para reserva.`,
      });
      return;
    }

    if (!name.split(" ")[1]) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário adicionar um sobrenome para reserva.`,
      });
      return;
    }

    if (phone.includes("_")) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: "Informe um número de telefone válido",
      });
      return;
    }

    if (
      !acceptTerm &&
      term !== "" &&
      term &&
      bookingConfig &&
      bookingConfig.useTerms
    ) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "É necessário aceitar os termos para continuar.",
      });
      return;
    }
    if (bookingConfig?.maxPeople && people > bookingConfig.maxPeople) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        html: `Há mais pessoas do que o permitido na reserva<br>(Máximo ${bookingConfig.maxPeople})`,
      });
      return;
    }
    if (!hourId) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário ter um horário para reserva`,
      });
      return;
    }
    if (!environmentId) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário ter um ambiente para reserva`,
      });
      return;
    }

    if (!email.includes("@")) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `E-mail inválido`,
      });
      return;
    }

    if (!bookingConfig?.id) {
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: `Erro ao encontrar configuração de reserva`,
      });
      return;
    }

    const hour = hours.find((h) => h.value === hourId);

    setLoading(true);
    const tk = await getToken();
    const pg = await getThisPluginspace(tk ? tk : "");
    if (pg && pg.gmt && hour?.select) {
      setLoading(true);
      const date = new Date(
        `${new Date().toString().split("GMT")[0]}${pg.gmt}`
      );
      if (
        date.getTime() >
        new Date(`${dateBooking} ${hour.select} GMT${pg.gmt}`).getTime()
      ) {
        Swal.fire({
          icon: "error",
          title: "Erro!",
          text: `Horário de reserva indisponível`,
        });
        setLoading(false);
        return;
      }
    }

    // Usa o resultado do cálculo de preço do backend
    if (requiresPayment && bookingConfig.paymentConfirm) {
      localStorage.setItem(
        "bookingToPay",
        JSON.stringify({
          date: dateBooking,
          bookingHourId: hourId,
          bookingEnvironmentId: environmentId,
          email,
          bookingConfigId: bookingConfig.id,
          people,
          name,
          phone: phone,
          hour: hour?.select,
          // Dados de preço calculados pelo backend
          calculatedPrice: calculatedPrice,
          priceInReais: priceInReais,
          priceType: priceType,
          priceBreakdown: priceBreakdown,
          accessibilityNeeds: accessibilityNeeds,
        })
      );
      setLoading(false);
      navigate(`/${pluginspace_id}/booking/payment`);
    } else {
      const subscriptionStorage = localStorage.getItem(
        window.btoa("subscription_notification")
      );
      let subscription = null;
      if (
        subscriptionStorage &&
        window.atob(subscriptionStorage) &&
        JSON.parse(window.atob(subscriptionStorage))
      ) {
        subscription = JSON.parse(window.atob(subscriptionStorage));
      }

      await api
        .post(
          "/api/queueReservation/booking",
          {
            date: dateBooking,
            bookingHourId: hourId,
            bookingEnvironmentId: environmentId,
            email,
            bookingConfigId: bookingConfig.id,
            people,
            name,
            phone: telephoneNoMask(phone),
            subscription,
            urlNotification:
              window.location.href + "/" + telephoneNoMask(phone),
            accessibilityNeeds: accessibilityNeeds,
          },
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then((response) => {
          setLoading(false);
          const bookingResponse = response.data;
          if (bookingResponse.success) {
            const redirectUrl = `/${pluginspace_id}/booking/success/${telephoneNoMask(
              phone
            )}`;
            try {
              console.log("Booking: navegando para:", redirectUrl);
              navigate(redirectUrl);
            } catch (e) {
              console.error("Booking: erro ao chamar navigate():", e);
            }
            // Mostrar feedback ao usuário (não bloqueia a tentativa de navegar)
            Swal.fire({
              icon: "success",
              title: "Sucesso!",
              text: "Você realizou sua reserva.",
            });
            // fallback: se por algum motivo o navigate não mudar a rota (mobile/cordova/prod), força redirect substituindo a entrada de histórico
            setTimeout(() => {
              if (!window.location.pathname.includes("/booking/success")) {
                console.warn(
                  "Booking: navigate falhou — forçando window.location.replace ->",
                  redirectUrl
                );
                window.location.replace(redirectUrl);
              }
            }, 600);
          } else {
            Swal.fire({
              icon: "error",
              title: "Erro ao realizar reserva!",
              text: "Não foi possível realizar reserva.",
            }).then(async () => await init());
          }
        })
        .catch((err) => {
          setLoading(false);
          let title = "Erro ao realizar reserva!";
          let text = "Não foi possível realizar reserva.";
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.error
          ) {
            title = err.response.data.error;
          }
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
          ) {
            text = err.response.data.message;
          }
          Swal.fire({
            icon: "error",
            title,
            text,
          }).then(async () => await init());
        });
    }
  }

  async function getConfig() {
    const configStorage = await getAppConfig();
    setConfig(configStorage);
  }

  function changePhone(newPhone: string) {
    const phoneValidated = newPhone.replaceAll("_", "");
    if (phoneValidated.length >= 14 && phoneValidated[13] !== "_") {
      setMaskTelephone("(99) 99999-9999");
    } else {
      setMaskTelephone("(99) 9999-9999");
    }
  }

  function changePhoneFind(newPhone: string) {
    const phoneValidated = newPhone.replaceAll("_", "");
    if (phoneValidated.length >= 14 && phoneValidated[13] !== "_") {
      setMaskTelephone("(99) 99999-9999");
    } else {
      setMaskTelephone("(99) 9999-9999");
    }
  }

  function filterHour(hour: string, date: string) {
    const now = new Date().getTime();
    const timeToCompare = new Date(`${date} ${hour}`).getTime();
    if (now > timeToCompare) return false;
    return true;
  }

  function changeDate(data: string) {
    setHourId(0);
    const fullData = new Date(data);
    const week = getWeekDay(fullData.getDay());
    let newHours = [{ value: 0, select: "Selecione um horário" }];
    if (bookingConfig?.days) {
      const hoursFind = bookingConfig.days.find((day) => day.day === week);
      if (
        hoursFind &&
        hoursFind.able &&
        hoursFind.hours &&
        hoursFind.hours.length > 0
      ) {
        const hoursNext = hoursFind.hours
          .map((hour) => ({ value: parseInt(hour.id), select: hour.hour }))
          .sort((a, b) =>
            parseInt(a.select.split(":")[0]) > parseInt(b.select.split(":")[0])
              ? 1
              : parseInt(b.select.split(":")[0]) >
                parseInt(a.select.split(":")[0])
                ? -1
                : 0
          )
          .filter((hour) => filterHour(hour.select, data));
        setHours([...newHours, ...hoursNext]);
        setDateBooking(data);
        return;
      }
    }
    Swal.fire({
      icon: "warning",
      title: "Atenção!",
      text: `Não há horários disponíveis para ${translateWeekday(week)}.`,
    });
    setHours(newHours);
    setDateBooking("");
  }

  async function validateNumber() {
    try {
      setLoading(true);

      if (phoneFind.includes("_")) {
        Swal.fire({
          icon: "warning",
          title: "Atenção!",
          text: "Informe um número de telefone válido",
        });
        return;
      }

      await api
        .get(
          `/api/queueReservation/booking/${bookingConfig?.id}/${telephoneNoMask(
            phoneFind
          )}`,
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then((response) => {
          const bookingResponse = response.data;

          if (
            bookingResponse &&
            bookingResponse.data &&
            bookingResponse.data.length > 0
          ) {
            navigate(
              `/${pluginspace_id}/booking/${telephoneNoMask(phoneFind)}`
            );
          } else {
            Swal.fire({
              icon: "warning",
              title: "Atenção!",
              text: "Não foi possível encontrar reservas com esse número de telefone.",
            });
          }
        })
        .catch((err) => {
          let title = "Erro!";
          let text =
            "Não foi possível encontrar reservas com esse número de telefone.";
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.error
          ) {
            title = err.response.data.error;
          }
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
          ) {
            text = err.response.data.message;
          }
          Swal.fire({
            icon: "error",
            title,
            text,
          });
        });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <BookingHeader />
      {/* Carousel section - positioned below header with 5% top margin */}
      {bookingConfig && bookingConfig.images && bookingConfig.images.length > 0 && (
        <div style={{
          width: '100%',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          paddingTop: 'clamp(24px, 5vw, 48px)',
          paddingBottom: '24px',
          paddingLeft: '24px',
          paddingRight: '24px',
          display: 'flex',
          justifyContent: 'center',
          boxSizing: 'border-box'
        }}>
          <div style={{
            position: "relative",
            width: "clamp(400px, 50vw, 600px)",
            aspectRatio: "16/9",
            borderRadius: "15px",
            overflow: "hidden",
            boxShadow: "0 12px 32px rgba(102, 126, 234, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
            border: "2px solid rgba(102, 126, 234, 0.2)"
          }}>
            <img
              src={bookingConfig.images[currentImageIndex].url}
              alt={`Imagem ${currentImageIndex + 1}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "opacity 10s ease-in-out"
              }}
            />
            <div style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.05) 100%)",
              pointerEvents: "none"
            }} />
            {bookingConfig.images.length > 1 && (
              <div style={{
                position: "absolute",
                bottom: "12px",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: "6px",
                zIndex: 10
              }}>
                {bookingConfig.images.map((_, index) => (
                  <div
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: index === currentImageIndex ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.4)",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: index === currentImageIndex ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "none"
                    }}
                  />
                ))}
              </div>
            )}
            {bookingConfig.images.length > 1 && (
              <>
                <button
                  onClick={() =>
                    setCurrentImageIndex(
                      (prevIndex) =>
                        (prevIndex - 1 + bookingConfig.images.length) %
                        bookingConfig.images.length
                    )
                  }
                  style={{
                    position: "absolute",
                    left: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(255, 255, 255, 0.7)",
                    border: "none",
                    borderRadius: "50%",
                    width: "34px",
                    height: "34px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "17px",
                    zIndex: 10,
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.7)";
                  }}
                >
                  ❮
                </button>
                <button
                  onClick={() =>
                    setCurrentImageIndex(
                      (prevIndex) =>
                        (prevIndex + 1) % bookingConfig.images.length
                    )
                  }
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(255, 255, 255, 0.7)",
                    border: "none",
                    borderRadius: "50%",
                    width: "34px",
                    height: "34px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "17px",
                    zIndex: 10,
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.7)";
                  }}
                >
                  ❯
                </button>
              </>
            )}
          </div>
        </div>
      )}
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        padding: '40px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowX: 'hidden',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {loading ? (
          <div style={{
            position: 'fixed',
            top: '80px',
            left: 0,
            right: 0,
            height: 'calc(100vh - 80px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(248, 249, 250, 0.95)',
            zIndex: 9999
          }}>
            <Loading type="spinningBubbles" color="#6366f1" width={64} height={64} />
          </div>
        ) : (
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            width: '100%'
          }}>
            {bookingConfig && (
              <div className="body-booking">
                {/*
                Evite usar window.innerWidth direto no render (não reage a resize e pode quebrar SSR/hydration).
                A responsividade é tratada via CSS em .booking-form-container.
              */}
                <div className="booking-form booking-form-container">
                  {/* Page Header */}
                  <div style={{
                    textAlign: "center",
                    marginBottom: "40px"
                  }}>
                    <div>
                      <h1 style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: "clamp(2rem, 5vw, 3rem)",
                        fontWeight: "800",
                        color: "#2d3436",
                        marginBottom: "12px",
                        letterSpacing: "-0.5px"
                      }}>
                        Faça sua <span style={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text"
                        }}>Reserva</span>
                      </h1>
                      <p style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "500",
                        fontSize: "16px",
                        color: "#636e72",
                        lineHeight: "1.6",
                        maxWidth: "500px",
                        margin: "0 auto"
                      }}>
                        Reserve seu espaço com facilidade. Escolha o ambiente, data e horário ideais para você.
                      </p>
                    </div>
                  </div>

                  {/* Formulário Unificado - Card com glass morphism */}
                  <div
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow = "0 12px 32px rgba(99, 102, 241, 0.22), 0 8px 16px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 8px 24px rgba(99, 102, 241, 0.15), 0 4px 12px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)";
                    }}
                    style={{
                      background: "linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.08) 50%, rgba(67, 56, 202, 0.1) 100%)",
                      backdropFilter: "blur(20px)",
                      WebkitBackdropFilter: "blur(20px)",
                      borderRadius: "20px",
                      padding: "32px",
                      marginBottom: "16px",
                      boxShadow: "0 8px 24px rgba(99, 102, 241, 0.15), 0 4px 12px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                      border: "1px solid rgba(99, 102, 241, 0.25)",
                      position: "relative" as const,
                      overflow: "hidden",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                  >
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
                      pointerEvents: "none"
                    }}></div>
                    <div style={{ position: "relative", zIndex: 1 }}>
                      <h3 style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "700",
                        fontSize: "22px",
                        marginBottom: "24px",
                        color: "#4338ca",
                        textAlign: "center",
                        letterSpacing: "0.5px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px"
                      }}>
                        <Sparkles size={20} color="#4338ca" />
                        Dados da Reserva
                      </h3>

                      {/* Data e Horário */}
                      <div style={{ marginBottom: "clamp(16px, 4vw, 24px)" }}>
                        {/* <h4 style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "600",
                        fontSize: "14px",
                        marginBottom: "12px",
                        color: "#4338ca",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}>
                        <Calendar size={16} color="#4338ca" />
                        <Clock size={16} color="#4338ca" />
                        Data e Horário
                      </h4> */}
                        <div className="responsive-row">
                          <div className="responsive-col">
                            <label style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: "600",
                              fontSize: "12px",
                              color: "#3730a3",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              marginBottom: "8px"
                            }}>
                              <Calendar size={14} color="#3730a3" />
                              Data
                            </label>
                            <input
                              id="date-input"
                              type="date"
                              value={dateBooking || ""}
                              onChange={(event) => {
                                setDateBooking(event.target.value);
                                setHours([
                                  { value: 0, select: "Selecione um horário" },
                                ]);
                              }}
                              onBlur={(event) => {
                                changeDate(event.target.value);
                              }}
                              min={new Date().toISOString().split("T")[0]}
                              className="field-input"
                            />
                          </div>
                          <div className="responsive-col">
                            <label style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: "600",
                              fontSize: "12px",
                              color: "#3730a3",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              marginBottom: "8px"
                            }}>
                              <Clock size={14} color="#3730a3" />
                              Horário
                            </label>
                            <select
                              value={hourId}
                              onChange={(event) =>
                                setHourId(parseInt(event.target.value))
                              }
                              className="field-input"
                            >
                              {hours.map((hour) => (
                                <option value={hour.value}>{hour.select}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Ambiente e Pessoas */}
                      <div style={{ marginBottom: "clamp(16px, 4vw, 24px)" }}>
                        {/* <h4 style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "600",
                        fontSize: "14px",
                        marginBottom: "12px",
                        color: "#4338ca",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}>
                        <MapPin size={16} color="#4338ca" />
                        <Users size={16} color="#4338ca" />
                        Local e Quantidade
                      </h4> */}
                        <div className="responsive-row">
                          <div className="responsive-col responsive-col--2">
                            <label style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: "600",
                              fontSize: "12px",
                              color: "#3730a3",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              marginBottom: "8px"
                            }}>
                              <MapPin size={14} color="#3730a3" />
                              Ambiente
                            </label>
                            {bookingConfig.environment &&
                              bookingConfig.environment.length > 0 ? (
                              <select
                                value={environmentId}
                                onChange={(event) =>
                                  setEnvironmentId(parseInt(event.target.value))
                                }
                                className="field-input"
                              >
                                <option value={0}>Escolha um ambiente</option>
                                {bookingConfig.environment.map((environment) => (
                                  <option value={environment.id}>
                                    {environment.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <select
                                value={environmentId}
                                onChange={(event) =>
                                  setEnvironmentId(parseInt(event.target.value))
                                }
                                className="field-input"
                              >
                                <option value={0}>Escolha um ambiente</option>
                              </select>
                            )}
                          </div>
                          <div className="responsive-col">
                            <label style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: "600",
                              fontSize: "12px",
                              color: "#3730a3",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              marginBottom: "8px"
                            }}>
                              <Users size={14} color="#3730a3" />
                              Qtd. Pessoas
                            </label>
                            <input
                              type="number"
                              value={people === 0 ? "" : people}
                              placeholder="0"
                              onChange={(event) =>
                                setPeople(parseInt(event.target.value))
                              }
                              max={bookingConfig.maxPeople}
                              className="field-input"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Dados Pessoais */}
                      <div>
                        {/* <h4 style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "600",
                        fontSize: "14px",
                        marginBottom: "12px",
                        color: "#4338ca",
                        textTransform: "uppercase",
                        letterSpacing: "1px"
                      }}> Dados Pessoais</h4> */}

                        {/* Nome Completo - Linha inteira */}
                        <div style={{ marginBottom: "clamp(12px, 3vw, 16px)" }}>
                          <label style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: "600",
                            fontSize: "12px",
                            color: "#3730a3",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            marginBottom: "8px"
                          }}>
                            <User size={14} color="#3730a3" />
                            Nome Completo
                          </label>
                          <input
                            type="text"
                            value={name}
                            onChange={(event) => {
                              const isValidName = /^[a-zA-ZÀ-ÿ\s]*$/.test(
                                event.target.value
                              );
                              if (isValidName) setName(event.target.value);
                            }}
                            className="field-input"
                          />
                        </div>

                        {/* Celular e E-mail - Lado a lado */}
                        <div className="responsive-row">
                          <div className="responsive-col">
                            <label style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: "600",
                              fontSize: "12px",
                              color: "#3730a3",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              marginBottom: "8px"
                            }}>
                              <Phone size={14} color="#3730a3" />
                              Celular
                            </label>
                            <InputMask
                              mask={maskTelephone}
                              type="text"
                              value={phone}
                              onChange={(event) => setPhone(event.target.value)}
                              className="field-input"
                            />
                          </div>
                          <div className="responsive-col">
                            <label style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: "600",
                              fontSize: "12px",
                              color: "#3730a3",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              marginBottom: "8px"
                            }}>
                              <Mail size={14} color="#3730a3" />
                              E-mail
                            </label>
                            <input
                              type="text"
                              value={email}
                              onChange={(event) => setEmail(event.target.value)}
                              className="field-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Preview de Preço com design moderno */}
                  {environmentId > 0 && people > 0 && (
                    <div className="price-preview"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-3px)";
                        e.currentTarget.style.boxShadow = "0 12px 32px rgba(76, 175, 80, 0.22), 0 8px 16px rgba(76, 175, 80, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 8px 24px rgba(76, 175, 80, 0.15), 0 4px 12px rgba(76, 175, 80, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)";
                      }}
                      style={{
                        padding: '24px',
                        margin: '0 0 16px 0',
                        background: "linear-gradient(135deg, rgba(76, 175, 80, 0.12) 0%, rgba(67, 160, 71, 0.08) 50%, rgba(56, 142, 60, 0.1) 100%)",
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        borderRadius: '20px',
                        boxShadow: "0 8px 24px rgba(76, 175, 80, 0.15), 0 4px 12px rgba(76, 175, 80, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                        border: '1px solid rgba(76, 175, 80, 0.25)',
                        position: "relative" as const,
                        overflow: "hidden",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                      }}>
                      <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
                        pointerEvents: "none"
                      }}></div>
                      <div style={{ position: "relative", zIndex: 1 }}>
                        {calculatingPrice ? (
                          <p style={{ margin: 0, color: '#388e3c', fontFamily: "'Poppins', sans-serif", fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <DollarSign size={16} color="#388e3c" />
                            Calculando preço...
                          </p>
                        ) : priceType === 'free' ? (
                          <p style={{ margin: 0, color: '#2e7d32', fontWeight: '800', fontFamily: "'Poppins', sans-serif", fontSize: 'clamp(14px, 3.5vw, 16px)' }}>
                            ✓ Reserva Gratuita
                          </p>
                        ) : requiresPayment && calculatedPrice !== null ? (
                          <div>
                            <p style={{ margin: '0 0 10px 0', fontSize: 'clamp(18px, 4vw, 20px)', fontWeight: '800', color: '#2e7d32', fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.3px', display: 'flex', alignItems: 'center', gap: 'clamp(6px, 1.5vw, 8px)' }}>
                              <DollarSign size={20} color="#2e7d32" />
                              Valor: R$ {priceInReais.toFixed(2)}
                            </p>
                            {priceType === 'byGroup' && priceBreakdown?.rangeApplied && (
                              <p style={{ margin: 0, fontSize: '13px', color: '#388e3c', fontFamily: "'Poppins', sans-serif", fontWeight: '500' }}>
                                Faixa: {priceBreakdown.rangeApplied.minPeople} a {priceBreakdown.rangeApplied.maxPeople} pessoas
                              </p>
                            )}
                            {priceType === 'perSeat' && (
                              <p style={{ margin: 0, fontSize: '13px', color: '#388e3c', fontFamily: "'Poppins', sans-serif", fontWeight: '500' }}>
                                {people} pessoa(s) × R$ {(priceBreakdown?.pricePerUnit / 100).toFixed(2)}
                              </p>
                            )}
                            {priceType === 'perEnvironment' && (
                              <p style={{ margin: 0, fontSize: '13px', color: '#388e3c', fontFamily: "'Poppins', sans-serif", fontWeight: '500' }}>
                                Preço fixo do ambiente
                              </p>
                            )}
                            {priceType === 'perTable' && priceBreakdown?.tablesCalculated && (
                              <p style={{ margin: 0, fontSize: '13px', color: '#388e3c', fontFamily: "'Poppins', sans-serif", fontWeight: '500' }}>
                                {priceBreakdown.tablesCalculated} mesa(s) × R$ {(priceBreakdown?.pricePerUnit / 100).toFixed(2).replace('.', ',')}
                                {priceBreakdown.seatsPerTable && (
                                  <span style={{ fontStyle: 'italic', color: '#66bb6a' }}>
                                    {' '}({priceBreakdown.seatsPerTable} lugares por mesa)
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}



                  {/* Necessidades de Acessibilidade - Card com glass morphism */}
                  <div
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow = "0 12px 32px rgba(103, 58, 183, 0.22), 0 8px 16px rgba(103, 58, 183, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 8px 24px rgba(103, 58, 183, 0.15), 0 4px 12px rgba(103, 58, 183, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)";
                    }}
                    style={{
                      background: "linear-gradient(135deg, rgba(103, 58, 183, 0.12) 0%, rgba(94, 53, 177, 0.08) 50%, rgba(81, 45, 168, 0.1) 100%)",
                      backdropFilter: "blur(20px)",
                      WebkitBackdropFilter: "blur(20px)",
                      borderRadius: "clamp(16px, 4vw, 20px)",
                      padding: "clamp(16px, 4vw, 24px)",
                      marginBottom: "clamp(12px, 3vw, 16px)",
                      boxShadow: "0 8px 24px rgba(103, 58, 183, 0.15), 0 4px 12px rgba(103, 58, 183, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                      border: "1px solid rgba(103, 58, 183, 0.25)",
                      position: "relative" as const,
                      overflow: "hidden",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box"
                    }}
                  >
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
                      pointerEvents: "none"
                    }}></div>
                    <div style={{ position: "relative", zIndex: 1 }}>
                      <h3 style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "700",
                        fontSize: "clamp(12px, 3vw, 14px)",
                        marginBottom: "clamp(6px, 1.5vw, 8px)",
                        color: "#673ab7",
                        textTransform: "uppercase",
                        letterSpacing: "1.2px",
                        display: "flex",
                        alignItems: "center",
                        gap: "clamp(6px, 1.5vw, 8px)"
                      }}>
                        <Accessibility size={16} color="#673ab7" />
                        Necessidades de Acessibilidade
                      </h3>
                      <p style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "400",
                        fontSize: "clamp(11px, 2.5vw, 13px)",
                        color: "#5e35b1",
                        marginBottom: "clamp(12px, 3vw, 16px)"
                      }}>
                        Selecione se sua reserva necessita de algum apoio especial
                      </p>

                      {AccessibilityOptions.map((option) => (
                        <AccessibilityComponent
                          key={option.value}
                          setAccessibilityNeeds={setAccessibilityNeeds}
                          accessibilityNeeds={option.value}
                          description={option.description}
                          accessibilityNeedsSelected={accessibilityNeeds}
                        />
                      ))}
                    </div>
                  </div>


                  {acceptTerm || !bookingConfig.useTerms ? (
                    <button
                      onClick={() => goBooking()}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 8px 24px rgba(102, 126, 234, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 16px rgba(102, 126, 234, 0.3)";
                      }}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "12px",
                        padding: "16px",
                        marginTop: "24px",
                        border: "none",
                        borderRadius: "16px",
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "600",
                        fontSize: "16px",
                        cursor: "pointer",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)"
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Confirmar Reserva
                    </button>
                  ) : (
                    <button
                      onClick={() => setOpenTerms(true)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-3px)";
                        e.currentTarget.style.boxShadow = "0 12px 32px rgba(103, 58, 183, 0.35), 0 8px 16px rgba(103, 58, 183, 0.25)";
                        e.currentTarget.style.background = "linear-gradient(135deg, #7e57c2 0%, #673ab7 50%, #5e35b1 100%)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 8px 24px rgba(103, 58, 183, 0.25), 0 4px 12px rgba(103, 58, 183, 0.15)";
                        e.currentTarget.style.background = "linear-gradient(135deg, #673ab7 0%, #5e35b1 50%, #512da8 100%)";
                      }}
                      style={{
                        width: "100%",
                        padding: "16px 24px",
                        marginTop: "24px",
                        background: "linear-gradient(135deg, #673ab7 0%, #5e35b1 50%, #512da8 100%)",
                        border: "none",
                        borderRadius: "16px",
                        color: "#ffffff",
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "700",
                        fontSize: "16px",
                        letterSpacing: "0.5px",
                        cursor: "pointer",
                        boxShadow: "0 8px 24px rgba(103, 58, 183, 0.25), 0 4px 12px rgba(103, 58, 183, 0.15)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        textTransform: "uppercase",
                        position: "relative" as const,
                        overflow: "hidden"
                      }}
                    >
                      <span style={{ position: "relative", zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <FileText size={16} />
                        Aceitar Termos de Consentimento
                      </span>
                    </button>
                  )}
                </div>
              </div>
            )}
            {openTerms && (
              <ModalTerms
                isOpen={openTerms}
                setIsOpen={setOpenTerms}
                termContent={term}
                backgroundButton={
                  config?.webappcolor.webappbutton &&
                  config.webappcolor.webappbutton
                }
                fontButton={
                  config?.webappcolor.fontbutton && config.webappcolor.fontbutton
                }
                acceptTerm={acceptTerm}
                setAcceptTerm={setAcceptTerm}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
